import type { BoardPattern } from "@/settings/types/types";

/**
 * The grid under the racks, sized and placed so it moves and scales with the
 * board rather than staying painted on the window behind it.
 *
 * Held to the desktop's own geometry (`@ceedero/canvas`'s
 * `resolveCanvasPatternGeometry`), which this application cannot import:
 * only `@ceedero/ui` is published. A 20-pixel tile at 100%, and a dot that
 * never shrinks below one device pixel — below that a radial gradient's hard
 * stop rasterizes inconsistently and the grid flickers in and out as the board
 * zooms.
 */
const BASE_TILE_SIZE_PIXELS = 20;
const BASE_DOT_RADIUS_PIXELS = 1;

export const resolveBoardPatternGeometry = ({
  originXPixels,
  originYPixels,
  pattern,
  zoomPercentage,
}: {
  originXPixels: number;
  originYPixels: number;
  pattern: BoardPattern;
  zoomPercentage: number;
}) => {
  const tileSizePixels = (BASE_TILE_SIZE_PIXELS * zoomPercentage) / 100;
  const tileSize = `${tileSizePixels}px ${tileSizePixels}px`;
  const isDotPattern = pattern === "dots";
  // A dot is drawn in the middle of its tile, so the tile is pulled back half a
  // tile to put a dot on the board's origin rather than beside it.
  const centeringOffsetPixels = isDotPattern ? tileSizePixels / 2 : 0;
  return {
    backgroundPosition: `${originXPixels - centeringOffsetPixels}px ${originYPixels - centeringOffsetPixels}px`,
    backgroundSize: isDotPattern ? tileSize : `${tileSize}, ${tileSize}`,
    dotRadiusPixels: isDotPattern
      ? Math.max((BASE_DOT_RADIUS_PIXELS * zoomPercentage) / 100, 1)
      : undefined,
  };
};
